import React, { useCallback, useEffect, useState, useMemo, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { motion, MotionConfig, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, ArrowUp, ArrowUpRight, Flag, Sparkles, X } from 'lucide-react';
import { useI18n } from '../../i18n';
import LanguageSwitcher from '../core/LanguageSwitcher';
import './login/loginPolish.css';
import LoginForm from './login/LoginForm';
import LoginInfo from './login/LoginInfo';
import LoginBackdrop from './login/LoginBackdrop';
import MobileWelcome from './login/MobileWelcome';
import MobileKartLifeNote from './login/MobileKartLifeNote';
import './login/loginMobile.css';
import KartLifeTeaser from './login/KartLifeTeaser';
import StorySection, { SectorSeam } from './login/StorySection';
import StatsStrip from './login/StatsStrip';
import { RaceHud, ProgressRail } from './login/JourneyOverlays';
import {
  prefersReducedMotion, supportsWebGL, sceneState,
  isWeakDevice, hasSlowVerdict, saveSlowVerdict, clearSlowVerdict,
} from './login/loginShared';

/* Three.js scene loads only on desktop, in its own chunk —
 * mobile never downloads it. */
const LoginScene = lazy(() => import('./login/LoginScene'));

/* If WebGL blows up at runtime, fall back to the static backdrop */
class SceneErrorBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? <LoginBackdrop /> : this.props.children;
  }
}

function useDesktop3D() {
  const [capable, setCapable] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(min-width: 1024px)').matches &&
    !prefersReducedMotion &&
    supportsWebGL()
  );
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = e => setCapable(e.matches && !prefersReducedMotion && supportsWebGL());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return capable;
}

/* Animations switch: the visitor's explicit choice ('on' | 'off') beats the
 * automatic verdict; with no choice made, weak or stuttering devices get the
 * static backdrop and a one-off notice. */
const ANIMATIONS_KEY = 'mykart_login_animations';
function useAnimations(can3D) {
  const [pref, setPref] = useState(() => {
    try { return localStorage.getItem(ANIMATIONS_KEY); } catch { return null; }
  });
  const [autoOff, setAutoOff] = useState(false);
  const [notice, setNotice] = useState(false);

  useEffect(() => {
    if (!can3D || pref) return;
    if (hasSlowVerdict()) setAutoOff(true);
    else if (isWeakDevice()) { saveSlowVerdict(); setAutoOff(true); setNotice(true); }
  }, [can3D, pref]);

  useEffect(() => {
    if (!notice) return undefined;
    const id = setTimeout(() => setNotice(false), 9000);
    return () => clearTimeout(id);
  }, [notice]);

  const on = pref === 'on' || (pref !== 'off' && !autoOff);
  const setOn = next => {
    try { localStorage.setItem(ANIMATIONS_KEY, next ? 'on' : 'off'); } catch { /* storage blocked */ }
    if (next) clearSlowVerdict();
    setPref(next ? 'on' : 'off');
    setNotice(false);
  };
  const onSlow = useCallback(() => { saveSlowVerdict(); setAutoOff(true); setNotice(true); }, []);
  return { on, setOn, notice, dismissNotice: () => setNotice(false), onSlow: pref === 'on' ? undefined : onSlow };
}

/* Warm the three.js chunk in idle time on desktop-class screens, so the
 * scene is cached even if the 3D mount happens later (e.g. window resize). */
function useScenePreload(active) {
  useEffect(() => {
    if (!active || !window.matchMedia('(min-width: 1024px)').matches || prefersReducedMotion || !supportsWebGL()) return undefined;
    const load = () => import('./login/LoginScene');
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(load, { timeout: 2500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(load, 2000);
    return () => clearTimeout(id);
  }, [active]);
}

/* Feeds normalized page scroll into the 3D scene choreography */
function useSceneScroll(active) {
  useEffect(() => {
    if (!active) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      sceneState.scroll = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
      sceneState.scroll = 0;
    };
  }, [active]);
}

export default function Login() {
  const { t, getRaw } = useI18n();
  const can3D = useDesktop3D();
  const animations = useAnimations(can3D);
  const animationsOn = animations.on;
  const use3D = can3D && animationsOn;
  useSceneScroll(use3D);
  useScenePreload(use3D);

  // The scroll story reuses the marketing copy from /dla-kartingowcow
  const storyItems = useMemo(() => (getRaw('kartingPage.features.items') || []).slice(0, 4), [getRaw]);

  const { scrollY } = useScroll();
  const hintOpacity = useTransform(scrollY, [0, 240], [1, 0]);

  return (
    <MotionConfig reducedMotion={animationsOn ? 'user' : 'always'}><div className="login-page" style={{ background: '#111827' }}>
      {/* Fixed background: 3D racing scene on desktop, static gradients elsewhere */}
      <div className="fixed inset-0 z-0">
        {use3D ? (
          <SceneErrorBoundary>
            <Suspense fallback={<LoginBackdrop />}>
              <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.2 }}
              >
                <LoginScene onSlow={animations.onSlow} />
              </motion.div>
            </Suspense>
          </SceneErrorBoundary>
        ) : (
          <LoginBackdrop />
        )}

        {/* Readability scrims over the scene */}
        {use3D && (
          <div className="absolute inset-0 pointer-events-none" aria-hidden>
            <div
              className="absolute inset-y-0 right-0 w-[46%]"
              style={{ background: 'linear-gradient(270deg, rgba(17,24,39,0.72), transparent)' }}
            />
            <div
              className="absolute inset-y-0 left-0 w-[38%]"
              style={{ background: 'linear-gradient(90deg, rgba(17,24,39,0.55), transparent)' }}
            />
            <div
              className="absolute inset-x-0 bottom-0 h-36"
              style={{ background: 'linear-gradient(0deg, rgba(17,24,39,0.65), transparent)' }}
            />
          </div>
        )}
      </div>

      {/* Top-right controls */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="login-language absolute right-5 flex items-center gap-2 z-[200]"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1.25rem)' }}
      >
        {can3D && (
          <button
            type="button"
            role="switch"
            aria-checked={animationsOn}
            onClick={() => animations.setOn(!animationsOn)}
            title={animationsOn ? t('Wyłącz animacje') : t('Włącz animacje')}
            className="login-motion-toggle flex items-center gap-2 px-2.5 py-1.5 rounded-xl transition-all hover:bg-white/10"
            style={{ border: '1px solid var(--glass-border)', color: 'var(--text-secondary)' }}
          >
            <Sparkles size={14} style={{ color: animationsOn ? 'var(--accent)' : 'var(--text-muted)' }} />
            <span className="text-xs font-semibold uppercase tracking-wide">{t('Animacje')}</span>
            <span className="login-motion-track" data-on={animationsOn}><span /></span>
          </button>
        )}
        {can3D && animations.notice && (
          <div role="status" className="login-motion-notice">
            <p>{t('Wyłączyliśmy animacje, żeby strona działała płynnie.')}</p>
            <div>
              <button type="button" onClick={() => animations.setOn(true)}>{t('Włącz mimo to')}</button>
              <button type="button" onClick={animations.dismissNotice} aria-label={t('Zamknij')}><X size={14} /></button>
            </div>
          </div>
        )}
        <LanguageSwitcher align="right" />
      </motion.div>

      {/* Journey overlays: live telemetry + progress rail (desktop 3D only) */}
      {use3D && (
        <>
          <RaceHud />
          <ProgressRail />
        </>
      )}

      <div className="login-shell relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-14">
        {/* HERO — info overlay + form */}
        <section
          className="login-hero min-h-screen flex items-center justify-center lg:justify-between gap-12 relative"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 2rem)', paddingBottom: '2.5rem' }}
        >
          <div className="login-desktop-info hidden lg:flex flex-1 self-stretch py-8">
            <LoginInfo />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
            className="login-form-column w-full max-w-[420px]"
          >
            <MobileWelcome />

            <LoginForm />
            <MobileKartLifeNote />


          </motion.div>

          {/* Scroll hint — desktop landing only */}
          {use3D && (
            <motion.div
              style={{ opacity: hintOpacity }}
              className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-1.5 pointer-events-none"
            >
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em]" style={{ color: 'var(--text-muted)' }}>
                {t('Zjedź niżej')}
              </span>
              <motion.span
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                style={{ color: 'var(--accent)' }}
              >
                <ChevronDown size={18} />
              </motion.span>
            </motion.div>
          )}
        </section>

        <KartLifeTeaser />

        {/* SCROLL STORY — the kart sets off, hunts down a rival and takes the flag */}
        {storyItems.map((_, i) => (
          <StorySection
            key={i}
            index={i}
            align={i % 2 === 0 ? 'right' : 'left'}
          />
        ))}

        {/* FINAL CTA */}
        <section className="login-finish hidden lg:flex min-h-screen flex-col items-center justify-center text-center relative">
          <SectorSeam label={t('Ostatnie okrążenie')} value="00:42.318" color="var(--accent)" icon={Flag} />
          {/* Soft backdrop so the headline never blends into the scene */}
          <div
            aria-hidden
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[620px] pointer-events-none"
            style={{ background: 'radial-gradient(closest-side, rgba(17,24,39,0.92), rgba(17,24,39,0.55) 55%, transparent)' }}
          />
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.5 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="relative flex flex-col items-center"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] mb-5" style={{ color: 'var(--text-muted)' }}>
              {t('Meta to dopiero początek')}
            </p>
            <h2 className="text-5xl xl:text-6xl font-black italic uppercase tracking-tight leading-[1.02]" style={{ color: 'var(--text-primary)' }}>
              {t('Wjedź do')}{' '}
              <span style={{ color: 'var(--accent)' }}>MyKart.pl</span>
            </h2>
            <p className="text-base mt-5 max-w-lg" style={{ color: 'var(--text-secondary)' }}>
              {t('kartingPage.hero.subdescription')}
            </p>
            <StatsStrip />
            <div className="flex items-center gap-4 mt-9">
              <button
                type="button"
                onClick={() => { document.getElementById('login-form')?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' }); document.getElementById('login-email')?.focus({ preventScroll: true }); }}
                className="btn-primary flex items-center gap-2 !px-7 !py-3.5 text-sm font-bold"
              >
                <ArrowUp size={16} />
                {t('Załóż darmowe konto')}
              </button>
              <Link
                to="/dla-kartingowcow"
                className="btn-secondary flex items-center gap-2 !px-6 !py-3.5 text-sm font-semibold"
              >
                {t('auth.marketing.link')}
                <ArrowUpRight size={15} />
              </Link>
            </div>
          </motion.div>
        </section>
      </div>
    </div></MotionConfig>
  );
}
